import Button from 'components/UI/V3/Button/Button';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import GenericModal from 'components/UI/V3/Modals/GenericModal';
import React from 'react';
import { isValidEmail } from 'utils/ValidationUtils';
import styles from './SendReportModal.module.scss';

interface SendReportModalProps {
    defaultEmail?: string;
    /* eslint-disable-next-line no-unused-vars */
    onConfirm: (email: string) => Promise<boolean>;
    onClose: () => void;
}

const SendReportModalComponent: React.FC<SendReportModalProps> = ({ defaultEmail, onConfirm, onClose }) => {
    const [email, setEmail] = React.useState<string>(defaultEmail ?? '');
    const [isSending, setIsSending] = React.useState(false);

    const trimmedEmail = email.trim();
    const isEmailValid = isValidEmail(trimmedEmail);
    const showEmailError = trimmedEmail.length > 0 && !isEmailValid;

    const handleSend = React.useCallback(async () => {
        if (isSending || !isEmailValid) return;
        setIsSending(true);
        const ok = await onConfirm(trimmedEmail);
        setIsSending(false);
        if (ok) onClose();
    }, [trimmedEmail, isEmailValid, isSending, onConfirm, onClose]);

    const footer = (
        <div className={styles.actions}>
            <Button
                label="Cancel"
                variant="outlined"
                size="large"
                fullWidth
                disabled={isSending}
                onClick={onClose}
            />
            <Button
                label="Send"
                variant="green"
                size="large"
                fullWidth
                isLoading={isSending}
                disabled={!isEmailValid}
                onClick={handleSend}
            />
        </div>
    );

    return (
        <GenericModal
            className={styles.modal}
            onClose={onClose}
            footer={footer}
        >
            <div className={styles.content}>
                <span className={styles.title}>Send report to:</span>
                <Input
                    type="email"
                    label="Email"
                    value={email}
                    placeholder="email@example.com"
                    error={showEmailError ? 'Enter a valid email address' : null}
                    onChange={event => setEmail(event.target.value)}
                    onEnterPressed={handleSend}
                    className={styles.input}
                />
            </div>
        </GenericModal>
    );
};

export const SendReportModal = React.memo(SendReportModalComponent);
